/**
 * Operator Preferences Hook
 *
 * Syncs operator UI preferences (pinned tabs, reporting favorites)
 * between localStorage (fast cache) and server (persistent storage).
 *
 * Features:
 * - Server-side persistence across devices/sessions
 * - localStorage as fast cache for offline support
 * - Timestamp-based conflict resolution (prefer newer)
 * - Corruption detection with automatic reset
 * - Migration of existing localStorage data on first sync
 *
 * Storage note: `STORAGE_KEYS.TABS_STATE` is the same key as the in-app tab bar uses.
 * `TabsContext` (`persistTabsState` in `persistence.ts`) merges `pinnedTabs` / `recentTabs` /
 * `tabHistories` / `activeTabPath` into that JSON. This hook also merges: `saveLocalPreferences`
 * read-modify-writes so we do not replace the entire blob in one write (avoids clobbering
 * tab fields the tab layer has just merged in, and preserves extra keys in the object).
 * Tab fields can be updated from either code path; keep that in mind for ordering/races.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { parseSharedPreferencesObject } from '@/context/TabsContext/persistence';
import { STORAGE_KEYS } from '@/config/tabsConfig';
import { trpc } from '@/trpc/client';
import { useAuth } from '@/context/AuthContext';

// ============================================================================
// Constants
// ============================================================================

const SYNC_DEBOUNCE_MS = 1000;
const MAX_FAVORITES_PER_CATEGORY = 10;
const MAX_RECENT_REPORTS_PER_CATEGORY = 5;

// ============================================================================
// Zod Schemas (must match backend)
// ============================================================================

const TabHistoryEntrySchema = z.object({
  path: z.string(),
  search: z.string().optional().default(''),
  state: z.unknown().optional(),
  timestamp: z.number()
});

const PersistedTabSchema = z.object({
  path: z.string(),
  title: z.string(),
  icon: z.string().optional(),
  pinnedOrder: z.number().optional(),
  lastAccessed: z.number(),
  historyIndex: z.number().optional().default(0),
  order: z.number().optional()
});

const RecentReportEntrySchema = z.object({
  reportType: z.string(),
  displayName: z.string(),
  category: z.string(),
  timestamp: z.number()
});

const OperatorPreferencesDataSchema = z.object({
  pinnedTabs: z.array(PersistedTabSchema).optional().default([]),
  recentTabs: z.array(PersistedTabSchema).optional().default([]),
  tabHistories: z.record(z.string(), z.array(TabHistoryEntrySchema)).optional().default({}),
  activeTabPath: z.string().nullable().optional(),
  reportingFavorites: z.record(z.string(), z.array(z.string())).optional().default({}),
  recentReports: z.record(z.string(), z.array(RecentReportEntrySchema)).optional().default({}),
  lastSyncedAt: z.number().optional()
});

export type OperatorPreferencesData = z.infer<typeof OperatorPreferencesDataSchema>;
export type PersistedTab = z.infer<typeof PersistedTabSchema>;
export type TabHistoryEntry = z.infer<typeof TabHistoryEntrySchema>;
export type RecentReportEntry = z.infer<typeof RecentReportEntrySchema>;

// ============================================================================
// Helper Functions
// ============================================================================

function getDefaultPreferences(): OperatorPreferencesData {
  return {
    pinnedTabs: [],
    recentTabs: [],
    tabHistories: {},
    activeTabPath: null,
    reportingFavorites: {},
    recentReports: {},
    lastSyncedAt: undefined
  };
}

/**
 * Validate and parse preferences from localStorage
 * Returns null if data is corrupted/invalid
 */
function parseLocalPreferences(): OperatorPreferencesData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    const result = OperatorPreferencesDataSchema.safeParse(parsed);

    if (result.success) {
      return result.data;
    }

    console.warn('[Preferences] Corrupted localStorage data detected:', result.error.message);
    return null;
  } catch (error) {
    console.warn('[Preferences] Failed to parse localStorage:', error);
    return null;
  }
}

/**
 * Save preferences to localStorage (read-modify-write, same key as the tab bar).
 */
function saveLocalPreferences(data: OperatorPreferencesData): void {
  try {
    const cur = localStorage.getItem(STORAGE_KEYS.TABS_STATE);
    const base = parseSharedPreferencesObject(cur);
    const merged: Record<string, unknown> = { ...base, ...data };
    localStorage.setItem(STORAGE_KEYS.TABS_STATE, JSON.stringify(merged));
  } catch (error) {
    console.error('[Preferences] Failed to save to localStorage:', error);
  }
}

/**
 * Migrate legacy localStorage data (tabs + reporting favorites)
 * into the unified preferences format
 */
function migrateLegacyData(): Partial<OperatorPreferencesData> | null {
  const migrated: Partial<OperatorPreferencesData> = {};
  let hasMigration = false;

  // In-app tab state: legacy `utx-ops-tabs-v1` is merged into `STORAGE_KEYS.TABS_STATE` at
  // the start of `restoreTabsState` in `persistence.ts` (before the tab bar hydrates).

  // Migrate legacy reporting favorites
  try {
    const legacyFavoritesKey = 'reporting_v2_favorites';
    const legacyFavorites = localStorage.getItem(legacyFavoritesKey);
    if (legacyFavorites) {
      const parsed = JSON.parse(legacyFavorites);
      if (typeof parsed === 'object' && parsed !== null) {
        migrated.reportingFavorites = parsed;
        hasMigration = true;
      }
      // Clear legacy data after migration
      localStorage.removeItem(legacyFavoritesKey);
      console.info('[Preferences] Migrated legacy reporting favorites');
    }
  } catch (error) {
    console.warn('[Preferences] Failed to migrate legacy favorites:', error);
  }

  // Migrate legacy recent reports
  try {
    const legacyRecentsKey = 'reporting_v2_recent_reports';
    const legacyRecents = localStorage.getItem(legacyRecentsKey);
    if (legacyRecents) {
      const parsed = JSON.parse(legacyRecents);
      if (typeof parsed === 'object' && parsed !== null) {
        migrated.recentReports = parsed;
        hasMigration = true;
      }
      // Clear legacy data after migration
      localStorage.removeItem(legacyRecentsKey);
      console.info('[Preferences] Migrated legacy recent reports');
    }
  } catch (error) {
    console.warn('[Preferences] Failed to migrate legacy recents:', error);
  }

  return hasMigration ? migrated : null;
}

// ============================================================================
// Main Hook
// ============================================================================

type UseOperatorPreferencesReturn = {
  /** Current preferences (from cache, updated on sync) */
  preferences: OperatorPreferencesData;
  /** Whether initial sync is in progress */
  isLoading: boolean;
  /** Whether a sync operation is in progress */
  isSyncing: boolean;
  /** Whether preferences are corrupted (triggers reset UI) */
  isCorrupted: boolean;
  /** Error message if sync failed */
  error: string | null;

  // Tab preferences
  setPinnedTabs: (tabs: PersistedTab[]) => void;
  setRecentTabs: (tabs: PersistedTab[]) => void;
  setTabHistories: (histories: Record<string, TabHistoryEntry[]>) => void;
  setActiveTabPath: (path: string | null) => void;

  // Reporting preferences
  setReportingFavorites: (category: string, favorites: string[]) => void;
  toggleReportingFavorite: (category: string, reportType: string) => string[];
  setRecentReports: (category: string, recents: RecentReportEntry[]) => void;
  addRecentReport: (category: string, report: RecentReportEntry) => void;
  getReportingFavorites: (category: string) => string[];
  getRecentReports: (category: string) => RecentReportEntry[];

  // Sync operations
  syncNow: () => Promise<void>;
  resetPreferences: () => Promise<void>;
};

export function useOperatorPreferences(): UseOperatorPreferencesReturn {
  const { initialState } = useAuth();
  const isAuthenticated = !!initialState?.currentUser;

  const [preferences, setPreferences] = useState<OperatorPreferencesData>(getDefaultPreferences);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCorrupted, setIsCorrupted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs to avoid stale closures
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncedRef = useRef<number>(0);
  const preferencesRef = useRef<OperatorPreferencesData>(preferences);
  const isSyncingRef = useRef(false);
  const initialSyncDoneRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  // tRPC mutations - stable references
  const syncMutation = trpc.preferences.sync.useMutation();
  const resetMutation = trpc.preferences.reset.useMutation();

  // tRPC query for initial fetch
  const { data: serverPrefs, isSuccess: serverPrefsLoaded } = trpc.preferences.get.useQuery(
    undefined,
    {
      enabled: isAuthenticated,
      staleTime: 60_000, // 1 minute
      refetchOnWindowFocus: false
    }
  );

  // ============================================================================
  // Initial Sync on Mount / Login
  // ============================================================================

  useEffect(() => {
    // Skip if not authenticated or already synced
    if (!isAuthenticated) {
      // Not logged in - use localStorage only
      const local = parseLocalPreferences();
      if (local) {
        setPreferences(local);
      }
      setIsLoading(false);
      return;
    }

    // Wait for server query to complete
    if (!serverPrefsLoaded) {
      return;
    }

    // Prevent duplicate syncs
    if (initialSyncDoneRef.current) {
      return;
    }
    initialSyncDoneRef.current = true;

    // Authenticated - merge local with server
    const performInitialSync = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check for legacy data to migrate
        const legacyData = migrateLegacyData();

        // Get current localStorage preferences
        let localPrefs = parseLocalPreferences();

        // If we have legacy data but no unified preferences, merge them
        if (legacyData && !localPrefs) {
          localPrefs = {
            ...getDefaultPreferences(),
            ...legacyData,
            lastSyncedAt: Date.now()
          };
          saveLocalPreferences(localPrefs);
        } else if (legacyData && localPrefs) {
          // Merge legacy into existing
          localPrefs = {
            ...localPrefs,
            pinnedTabs: legacyData.pinnedTabs ?? localPrefs.pinnedTabs,
            recentTabs: legacyData.recentTabs ?? localPrefs.recentTabs,
            tabHistories: { ...(localPrefs.tabHistories ?? {}), ...(legacyData.tabHistories ?? {}) },
            reportingFavorites: { ...(localPrefs.reportingFavorites ?? {}), ...(legacyData.reportingFavorites ?? {}) },
            recentReports: { ...(localPrefs.recentReports ?? {}), ...(legacyData.recentReports ?? {}) },
            lastSyncedAt: Date.now()
          };
          saveLocalPreferences(localPrefs);
        }

        // If server data is corrupted, use local and reset server
        if (serverPrefs?.corrupted) {
          console.warn('[Preferences] Server data corrupted, syncing local to server');
          setIsCorrupted(true);

          if (localPrefs) {
            // Sync local to server
            const result = await syncMutation.mutateAsync({
              localPreferences: localPrefs,
              localTimestamp: localPrefs.lastSyncedAt
            });
            setPreferences(result.preferences);
            saveLocalPreferences(result.preferences);
          }
          setIsLoading(false);
          return;
        }

        setIsCorrupted(false);

        // Determine what to sync
        if (!serverPrefs?.preferences && localPrefs) {
          // No server data - push local to server
          const result = await syncMutation.mutateAsync({
            localPreferences: localPrefs,
            localTimestamp: localPrefs.lastSyncedAt
          });
          setPreferences(result.preferences);
          saveLocalPreferences(result.preferences);
          lastSyncedRef.current = result.syncedAt;
        } else if (serverPrefs?.preferences && !localPrefs) {
          // No local data - use server data
          setPreferences(serverPrefs.preferences);
          saveLocalPreferences(serverPrefs.preferences);
          lastSyncedRef.current = Date.now();
        } else if (serverPrefs?.preferences && localPrefs) {
          // Both exist - sync with conflict resolution
          const result = await syncMutation.mutateAsync({
            localPreferences: localPrefs,
            localTimestamp: localPrefs.lastSyncedAt
          });
          setPreferences(result.preferences);
          saveLocalPreferences(result.preferences);
          lastSyncedRef.current = result.syncedAt;
        } else {
          // Neither exists - use defaults
          setPreferences(getDefaultPreferences());
        }
      } catch (err) {
        console.error('[Preferences] Initial sync failed:', err);
        setError(err instanceof Error ? err.message : 'Sync failed');

        // Fall back to localStorage
        const local = parseLocalPreferences();
        if (local) {
          setPreferences(local);
        }
      } finally {
        setIsLoading(false);
      }
    };

    performInitialSync();
  }, [isAuthenticated, serverPrefsLoaded]); // Removed unstable dependencies

  // ============================================================================
  // Debounced Sync to Server (using refs to avoid stale closures)
  // ============================================================================

  const scheduleDebouncedSync = useCallback(() => {
    if (!isAuthenticated) return;
    if (isSyncingRef.current) return; // Prevent concurrent syncs

    // Clear existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync - reads from ref to get latest preferences
    syncTimeoutRef.current = setTimeout(async () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      setIsSyncing(true);

      try {
        // Read current preferences from ref (not closure)
        const currentPrefs = preferencesRef.current;
        const result = await syncMutation.mutateAsync({
          localPreferences: currentPrefs,
          localTimestamp: currentPrefs.lastSyncedAt
        });
        lastSyncedRef.current = result.syncedAt;
        setError(null);
      } catch (err) {
        console.error('[Preferences] Debounced sync failed:', err);
        setError(err instanceof Error ? err.message : 'Sync failed');
      } finally {
        isSyncingRef.current = false;
        setIsSyncing(false);
      }
    }, SYNC_DEBOUNCE_MS);
  }, [isAuthenticated, syncMutation]);

  // ============================================================================
  // Update Helpers (optimistic + debounced sync)
  // ============================================================================

  const updatePreferences = useCallback(
    (updater: (prev: OperatorPreferencesData) => OperatorPreferencesData) => {
      setPreferences((prev) => {
        const next = {
          ...updater(prev),
          lastSyncedAt: Date.now()
        };
        // Optimistic localStorage update
        saveLocalPreferences(next);
        return next;
      });
      // Schedule server sync (reads from ref, not closure)
      scheduleDebouncedSync();
    },
    [scheduleDebouncedSync]
  );

  // ============================================================================
  // Tab Preferences API
  // ============================================================================

  const setPinnedTabs = useCallback(
    (tabs: PersistedTab[]) => {
      updatePreferences((prev) => ({ ...prev, pinnedTabs: tabs }));
    },
    [updatePreferences]
  );

  const setRecentTabs = useCallback(
    (tabs: PersistedTab[]) => {
      updatePreferences((prev) => ({ ...prev, recentTabs: tabs }));
    },
    [updatePreferences]
  );

  const setTabHistories = useCallback(
    (histories: Record<string, TabHistoryEntry[]>) => {
      updatePreferences((prev) => ({ ...prev, tabHistories: histories }));
    },
    [updatePreferences]
  );

  const setActiveTabPath = useCallback(
    (path: string | null) => {
      updatePreferences((prev) => ({ ...prev, activeTabPath: path }));
    },
    [updatePreferences]
  );

  // ============================================================================
  // Reporting Preferences API
  // ============================================================================

  const setReportingFavorites = useCallback(
    (category: string, favorites: string[]) => {
      updatePreferences((prev) => ({
        ...prev,
        reportingFavorites: {
          ...(prev.reportingFavorites ?? {}),
          [category]: favorites.slice(0, MAX_FAVORITES_PER_CATEGORY)
        }
      }));
    },
    [updatePreferences]
  );

  const toggleReportingFavorite = useCallback(
    (category: string, reportType: string): string[] => {
      // Calculate new favorites synchronously from current ref value
      const current = preferencesRef.current.reportingFavorites?.[category] ?? [];
      const index = current.indexOf(reportType);
      
      let newFavorites: string[];
      if (index > -1) {
        newFavorites = current.filter((_, i) => i !== index);
      } else {
        newFavorites = [reportType, ...current].slice(0, MAX_FAVORITES_PER_CATEGORY);
      }

      // Update state
      updatePreferences((prev) => ({
        ...prev,
        reportingFavorites: {
          ...(prev.reportingFavorites ?? {}),
          [category]: newFavorites
        }
      }));

      return newFavorites;
    },
    [updatePreferences]
  );

  const setRecentReports = useCallback(
    (category: string, recents: RecentReportEntry[]) => {
      updatePreferences((prev) => ({
        ...prev,
        recentReports: {
          ...(prev.recentReports ?? {}),
          [category]: recents.slice(0, MAX_RECENT_REPORTS_PER_CATEGORY)
        }
      }));
    },
    [updatePreferences]
  );

  const addRecentReport = useCallback(
    (category: string, report: RecentReportEntry) => {
      updatePreferences((prev) => {
        const current = prev.recentReports?.[category] ?? [];
        // Remove if already exists
        const filtered = current.filter((r) => r.reportType !== report.reportType);
        // Add to front
        const updated = [report, ...filtered].slice(0, MAX_RECENT_REPORTS_PER_CATEGORY);

        return {
          ...prev,
          recentReports: {
            ...(prev.recentReports ?? {}),
            [category]: updated
          }
        };
      });
    },
    [updatePreferences]
  );

  const getReportingFavorites = useCallback(
    (category: string): string[] => {
      return preferences.reportingFavorites?.[category] ?? [];
    },
    [preferences.reportingFavorites]
  );

  const getRecentReports = useCallback(
    (category: string): RecentReportEntry[] => {
      return preferences.recentReports?.[category] ?? [];
    },
    [preferences.recentReports]
  );

  // ============================================================================
  // Manual Sync & Reset
  // ============================================================================

  const syncNow = useCallback(async () => {
    if (!isAuthenticated) return;
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;
    setIsSyncing(true);
    setError(null);

    try {
      const currentPrefs = preferencesRef.current;
      const result = await syncMutation.mutateAsync({
        localPreferences: currentPrefs,
        localTimestamp: currentPrefs.lastSyncedAt
      });
      setPreferences(result.preferences);
      saveLocalPreferences(result.preferences);
      lastSyncedRef.current = result.syncedAt;
    } catch (err) {
      console.error('[Preferences] Manual sync failed:', err);
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isAuthenticated, syncMutation]);

  const resetPreferences = useCallback(async () => {
    if (isSyncingRef.current) return;
    
    isSyncingRef.current = true;
    setIsSyncing(true);
    setError(null);

    try {
      if (isAuthenticated) {
        const result = await resetMutation.mutateAsync();
        setPreferences(result.preferences);
        saveLocalPreferences(result.preferences);
      } else {
        const defaults = getDefaultPreferences();
        setPreferences(defaults);
        saveLocalPreferences(defaults);
      }
      setIsCorrupted(false);
    } catch (err) {
      console.error('[Preferences] Reset failed:', err);
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isAuthenticated, resetMutation]);

  // ============================================================================
  // Cleanup
  // ============================================================================

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return {
    preferences,
    isLoading,
    isSyncing,
    isCorrupted,
    error,

    // Tab preferences
    setPinnedTabs,
    setRecentTabs,
    setTabHistories,
    setActiveTabPath,

    // Reporting preferences
    setReportingFavorites,
    toggleReportingFavorite,
    setRecentReports,
    addRecentReport,
    getReportingFavorites,
    getRecentReports,

    // Sync operations
    syncNow,
    resetPreferences
  };
}
